import classNames from 'classnames';
import Form from 'components/formElements/Form';
import FormInput from 'components/formElements/FormInput';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import timezones from 'timezones-list';
import { inputWarningClass } from '../../helpers/CssClasses';
import { EMAIL_REGEX } from '../../helpers/Validators';
import { signup, signupComplete } from '../../services/User';
import { AlertType } from '../alert/Alert';
import Button from '../Button';
import FormField from '../formElements/FormField';
import FormFieldDescription from '../formElements/FormFieldDescription';
import FormFieldError from '../formElements/FormFieldError';
import FormLabel from '../formElements/FormLabel';
import Select from '../formElements/Select';
import Spinner from '../spinner/Spinner';
import Password from 'components/formElements/Password';
import UseAlert from 'hooks/UseAlert';

interface FormModel {
  name: string;
  email: string;
  password: string;
  timezone: string;
}

export default function RegisterForm(): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [completeRegistration, setCompleteRegistration] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { alertElement: errorAlertElement, setMessage: setErrorMessage } = UseAlert(AlertType.Danger);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormModel>({ defaultValues: { timezone: 'Europe/Amsterdam' } });

  const timezonesList = useMemo(
    () =>
      timezones
        .sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }

          // names must be equal
          return 0;
        })
        .map(val => ({
          label: val.tzCode,
          value: val.tzCode,
        })),
    [],
  );

  /**
   * Function that be called when submitting the form
   */
  const onSubmit = async ({ name, email, password, timezone }: FormModel) => {
    try {
      setSubmitting(true);
      await signup(name, email, password, timezone);

      setErrorMessage('');
      navigate('/#signup_success');
    } catch (error) {
      setErrorMessage(error as string);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Complete registration
   */
  useEffect(() => {
    if (location.hash) {
      const abortController = new AbortController();
      setCompleteRegistration(true);
      const token = location.hash.replace('#token=', '');
      signupComplete(token, abortController.signal)
        .then(() => navigate('/#signup_complete'))
        .catch(e => {
          if (!abortController.signal.aborted) {
            navigate('/#signup_failed_invalid_token');
            console.error(e);
          }
        });

      return () => abortController.abort();
    }
  }, [location, navigate, setErrorMessage]);

  /**
   * Show a spinner while we complete the registration
   */
  if (completeRegistration) {
    return (
      <div className='flex items-center'>
        <Spinner inverseColor={true} className='mr-2' /> <span className='text-sm'>complete registration</span>
      </div>
    );
  }

  return (
    <>
      {errorAlertElement}

      <Form onSubmit={handleSubmit(onSubmit)} id='registerForm'>
        <FormField>
          <FormLabel htmlFor='name'>Name</FormLabel>
          <FormInput error={errors.name} placeholder='John Doe' {...register('name', { required: 'this field is required' })} />
        </FormField>

        <FormField>
          <FormLabel htmlFor='email'>Email</FormLabel>
          <FormInput
            error={errors.email}
            placeholder='john@doe.com'
            type='email'
            {...register('email', {
              required: 'this field is required',
              pattern: {
                value: EMAIL_REGEX,
                message: 'Incorrect e-mail address',
              },
            })}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor='timezone'>Timezone</FormLabel>
          <FormFieldDescription>
            The timezone is needed to set the correct dates across the application. It also set the correct time for sending the push
            notifications
          </FormFieldDescription>
          <Select
            id='timezone'
            options={timezonesList}
            className={classNames({ [inputWarningClass]: errors.name })}
            {...register('timezone', { required: 'this field is required', value: 'Europe/Amsterdam' })}
          />
          {errors.timezone && <FormFieldError>{errors.timezone.message}</FormFieldError>}
        </FormField>

        <FormField>
          <FormLabel htmlFor='password'>Password</FormLabel>
          <Password
            onChangeScore={() => {
              // have noop FN shows the password indicator
              // setPasswordScore(score)
            }}
            newPassword={true}
            error={errors.password}
            {...register('password', {
              required: 'this field is required',
              validate: value => {
                if (value.length < 7) {
                  return 'Password should at least contains 6 chars';
                }
              },
            })}
          />
        </FormField>

        <div className='flex justify-center my-3'>
          <Button htmlForm='registerForm' type='primary' fullWidth={true} loading={submitting}>
            Register
          </Button>
        </div>

        <div className='flex justify-center items-center text-gray-700 dark:text-gray-200'>
          <Link to='/' className='text-sm mr-2'>
            Already have an account?
          </Link>
        </div>
      </Form>
    </>
  );
}
